"use client";

import { cn } from "@/lib/utils";

/*
 * Hero preview — the "render bay".
 *
 * A REPRESENTATIVE before → after of a dealership website (no live screenshot,
 * no fetch). The "before" is a dated, washed-out mock. When the visitor submits
 * their URL in the lead form, `assembled` flips true and the "after" mock's
 * blocks fade + rise into place, staggered by a `--i` index var driving the
 * shared `mkt-assemble` keyframe (defined in globals.css).
 *
 * Reduced motion: the `mkt-assemble-item` class is animation-suppressed in
 * globals.css under prefers-reduced-motion, and we also drop the entrance
 * delay/duration to zero — so the assembled state simply appears.
 *
 * This wrapper carries `.marketing-light`, flipping neutral tokens to the
 * bright register for the whole right column. Token classes only.
 */

function AssembleBlock({
  index,
  assembled,
  className,
  children,
}: {
  index: number;
  assembled: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "mkt-assemble-item",
        assembled ? "[animation:mkt-assemble_0.55s_cubic-bezier(0.22,1,0.36,1)_both]" : "opacity-0",
        className,
      )}
      style={
        assembled
          ? // Stagger: each block waits (index * 90ms) before assembling.
            ({ animationDelay: `calc(var(--i) * 90ms)`, "--i": index } as React.CSSProperties)
          : undefined
      }
    >
      {children}
    </div>
  );
}

export function HeroPreview({ assembled }: { assembled: boolean }) {
  return (
    <div className="relative">
      {/* Browser chrome — the frame the site renders inside. The .marketing-light
          scope is on the CARD only, so the caption below it stays in the dark
          page scope and reads as light-on-dark. */}
      <div className="marketing-light overflow-hidden rounded-(--radius-xl) border border-(--color-border-strong) bg-(--color-surface-1) shadow-(--shadow-card)">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-(--color-border) bg-(--color-surface-2) px-4 py-3">
          <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
          <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
          <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
          <div className="ml-3 flex-1 rounded-(--radius-full) bg-(--color-surface-3) px-3 py-1">
            <span className="font-mono text-[10px] tracking-wide text-(--color-foreground-subtle)">
              {assembled ? "yourdealership.com" : "yourdealership.com — old site"}
            </span>
          </div>
          <span
            className={cn(
              "font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-500",
              assembled ? "text-(--color-brand-accent)" : "text-(--color-foreground-faint)",
            )}
          >
            {assembled ? "● live" : "before"}
          </span>
        </div>

        {/* Viewport — the two states are cross-faded. */}
        <div className="relative aspect-[4/5] sm:aspect-[4/3.4]">
          {/* BEFORE: dated, plain, washed out. */}
          <div
            aria-hidden={assembled}
            className={cn(
              "absolute inset-0 flex flex-col gap-3 p-5 transition-opacity duration-500",
              assembled ? "pointer-events-none opacity-0" : "opacity-100",
            )}
          >
            <div className="flex items-center justify-between">
              <div className="h-3.5 w-24 rounded-(--radius-xs) bg-(--color-foreground-subtle)/55" />
              <div className="flex gap-2">
                <div className="h-2.5 w-10 rounded-(--radius-xs) bg-(--color-border-strong)" />
                <div className="h-2.5 w-10 rounded-(--radius-xs) bg-(--color-border-strong)" />
                <div className="h-2.5 w-10 rounded-(--radius-xs) bg-(--color-border-strong)" />
              </div>
            </div>
            <div className="mt-1 flex flex-1 flex-col items-center justify-center gap-2 rounded-(--radius-xs) border border-dashed border-(--color-border-strong) bg-(--color-surface-2)">
              <div className="h-2.5 w-40 rounded-(--radius-xs) bg-(--color-border-strong)" />
              <div className="h-2.5 w-28 rounded-(--radius-xs) bg-(--color-border-strong)" />
              <div className="mt-2 h-5 w-24 rounded-(--radius-xs) bg-(--color-foreground-subtle)/40" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="h-12 rounded-(--radius-xs) bg-(--color-surface-2)" />
              <div className="h-12 rounded-(--radius-xs) bg-(--color-surface-2)" />
              <div className="h-12 rounded-(--radius-xs) bg-(--color-surface-2)" />
            </div>
          </div>

          {/* AFTER: polished, self-assembling. */}
          <div
            aria-hidden={!assembled}
            className={cn(
              "absolute inset-0 flex flex-col gap-3 p-5 transition-opacity duration-300",
              assembled ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {/* Nav */}
            <AssembleBlock
              index={0}
              assembled={assembled}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <div className="size-5 rounded-(--radius-sm) bg-(--color-brand-accent)" />
                <div className="h-3 w-20 rounded-(--radius-xs) bg-(--color-foreground)" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-2 w-8 rounded-(--radius-xs) bg-(--color-foreground-subtle)" />
                <div className="h-2 w-8 rounded-(--radius-xs) bg-(--color-foreground-subtle)" />
                <div className="h-6 w-14 rounded-(--radius-full) bg-(--color-brand-accent)" />
              </div>
            </AssembleBlock>

            {/* Hero banner */}
            <AssembleBlock
              index={1}
              assembled={assembled}
              className="relative flex min-h-[38%] flex-col justify-center gap-2 overflow-hidden rounded-(--radius-md) bg-(--color-foreground) p-5"
            >
              <div
                aria-hidden
                className="absolute -right-8 -top-10 size-40 rounded-(--radius-full) bg-(--color-brand-accent) opacity-30 blur-2xl"
              />
              <div className="relative h-3 w-2/3 rounded-(--radius-xs) bg-(--color-background)" />
              <div className="relative h-3 w-2/5 rounded-(--radius-xs) bg-(--color-background)/70" />
              <div className="relative mt-2 h-6 w-28 rounded-(--radius-full) bg-(--color-brand-accent)" />
            </AssembleBlock>

            {/* Vehicle cards */}
            <AssembleBlock index={2} assembled={assembled} className="grid grid-cols-3 gap-2.5">
              {[0, 1, 2].map((c) => (
                <div
                  key={c}
                  className="overflow-hidden rounded-(--radius-sm) border border-(--color-border) bg-(--color-surface-1)"
                >
                  <div className="h-9 bg-(--color-surface-3)" />
                  <div className="flex flex-col gap-1.5 p-2">
                    <div className="h-1.5 w-full rounded-(--radius-xs) bg-(--color-foreground-subtle)" />
                    <div className="h-1.5 w-2/3 rounded-(--radius-xs) bg-(--color-brand-accent)" />
                  </div>
                </div>
              ))}
            </AssembleBlock>

            {/* Trust bar / CTA */}
            <AssembleBlock
              index={3}
              assembled={assembled}
              className="flex items-center justify-between rounded-(--radius-sm) border border-(--color-border) bg-(--color-surface-2) px-3 py-2.5"
            >
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-(--radius-full) bg-(--color-brand-accent)" />
                <div className="h-2 w-24 rounded-(--radius-xs) bg-(--color-foreground-subtle)" />
              </div>
              <div className="h-5 w-16 rounded-(--radius-full) bg-(--color-foreground)" />
            </AssembleBlock>
          </div>
        </div>
      </div>

      {/* Caption — what just happened. */}
      <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.16em] text-(--color-foreground-muted)">
        {assembled ? "After — rebuilt in seconds" : "Before — drop in your URL →"}
      </p>
    </div>
  );
}
